/**
 * Schema-to-FormFieldDef conversion and form-related text utilities.
 * Interactive form rendering itself lives in McpForm (components/form/McpForm.tsx).
 */

import type { FormFieldDef } from '../types';
import { resolveWidgetLocale } from './locale';
import { store } from '../state/store';

/**
 * Turn a form spec's still-missing fields into a plain-text bullet list.
 * No intro sentence here - unlike ChatOrchestrator.buildMissingRequiredTextPrompt
 * (which replaces the whole message and needs its own lead-in), this substitutes
 * inline for a ```form``` block inside the AI's own surrounding prose, which
 * already supplies the lead-in ("please fill out the following:" etc).
 */
function formSpecToTextPrompt(
  fields: Record<string, unknown>,
  labels: Record<string, unknown>,
): string {
  const missingLabels = Object.entries(labels)
    .filter(([name, label]) => {
      if (typeof label !== 'string' || !label) return false;
      const known = fields[name];
      return known === undefined || known === null || known === '';
    })
    .map(([, label]) => label as string);

  if (missingLabels.length === 0) return '';

  return missingLabels.map((l) => `- ${l}`).join('\n');
}

/**
 * Replace ```form``` blocks the widget shouldn't render as interactive UI:
 * just the ones whose "tool" (per the {"tool":"ToolName","fields":{...},"labels":{...}}
 * spec the AI is instructed to emit) has forms disabled for that specific tool.
 * Enforced by parsing the block's own JSON rather than trusting the model to
 * have honored the prompt. A disabled-tool block is converted into a plain
 * text question for its still-missing fields, instead of just being deleted,
 * so the assistant's surrounding message doesn't dangle with no question.
 */
export function stripDisabledFormBlocks(content: string): string {
  const { tools } = store.getState();
  return content.replace(/```form([\s\S]*?)```/g, (block, body) => {
    let spec: { tool?: unknown; fields?: unknown; labels?: unknown };
    try {
      spec = JSON.parse(body.trim());
    } catch {
      return block;
    }
    if (typeof spec.tool !== 'string') return block;
    const tool = tools.find((tl) => tl.name === spec.tool);
    if (tool?.formEnabled !== false) return block;

    const fields = spec.fields && typeof spec.fields === 'object' ? spec.fields as Record<string, unknown> : {};
    const labels = spec.labels && typeof spec.labels === 'object' ? spec.labels as Record<string, unknown> : {};
    return formSpecToTextPrompt(fields, labels);
  });
}

const FORM_FENCE_RE = /```form[\s\S]*?```/;

/**
 * Matches an imperative instruction to fill out "the form" (English or Hebrew
 * phrasing) so it can be swapped for neutral wording when no real form was
 * actually emitted. Deliberately narrow - only the "go fill this out" command,
 * not every sentence that happens to contain the word "form".
 */
const MISLEADING_FORM_PROMPTS: Array<{ pattern: RegExp; replacement: string }> = [
  {
    pattern: /\b(?:please\s+)?(?:fill\s*(?:out|in)?|complete)\s+(?:out\s+)?the\s+form\b(?:\s+below)?[^.!?\n]*[.:!?]?/gi,
    replacement: 'Please provide the following details:',
  },
  {
    pattern: /(?:נא\s+)?(?:מלאי?|למלא|השלימי?)\s+את\s+ה?טופס[^.!?\n:]*[.:!?]?/g,
    replacement: 'אנא ציין/י את הפרטים הבאים:',
  },
];

/**
 * Defensive safeguard for when the model promises "the form" in prose without
 * ever emitting a ```form``` block (a prompt-adherence failure, not a rendering
 * one - see webchat_regular_form_instructions). Only touches the specific
 * misleading sentence; leaves every other mention of "form"/"טופס" untouched,
 * so legitimate discussion of a form already shown elsewhere is unaffected.
 */
export function normalizeMisleadingFormMention(text: string): string {
  if (FORM_FENCE_RE.test(text)) return text;

  let result = text;
  for (const { pattern, replacement } of MISLEADING_FORM_PROMPTS) {
    result = result.replace(pattern, replacement);
  }
  return result;
}

/** <input type="date"> only accepts YYYY-MM-DD — truncate a full ISO timestamp if one arrives. */
export function normalizeDateValue(v: unknown): string {
  if (typeof v !== 'string') return '';
  const match = v.match(/^\d{4}-\d{2}-\d{2}/);
  if (match) return match[0];
  const parsed = new Date(v);
  return Number.isNaN(parsed.getTime()) ? '' : parsed.toISOString().slice(0, 10);
}

/**
 * Convert a property name to a human-readable label.
 * - snake_case: first_name → "First Name"
 * - camelCase: organizationName → "Organization Name"
 * - ALL_CAPS stays as-is (likely an acronym): MATNR → "MATNR"
 */
function humanizeLabel(name: string): string {
  // All-caps (with optional underscores) - likely an acronym, keep as-is
  if (/^[A-Z0-9_]+$/.test(name)) return name;

  return name
    // Insert space before uppercase letters in camelCase
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    // Replace underscores/hyphens with spaces
    .replace(/[_-]+/g, ' ')
    // Capitalize first letter of each word
    .replace(/\b\w/g, c => c.toUpperCase())
    .trim();
}

/**
 * Convert a JSON Schema's properties into FormFieldDef[].
 * Reads `properties`, `required`, and uses `title`/`description` for labels.
 */
export function schemaToFields(
  inputSchema: Record<string, unknown>,
  values?: Record<string, unknown>,
): FormFieldDef[] {
  const properties = inputSchema.properties as Record<string, Record<string, unknown>> | undefined;
  if (!properties) return [];

  const requiredArr = (inputSchema.required as string[]) || [];
  const fields: FormFieldDef[] = [];

  const locale = resolveWidgetLocale();

  for (const [name, prop] of Object.entries(properties)) {
    const propType = prop.type as string | undefined;
    const format = prop.format as string | undefined;
    const contentMediaType = prop.contentMediaType as string | undefined;
    const enumVals = prop.enum as string[] | undefined;
    const enumNames = prop.enumNames as string[] | undefined;
    // Pick localized title/placeholder if available (e.g. title_he, placeholder_he)
    const localizedTitle = locale !== 'en' ? prop[`title_${locale}`] as string | undefined : undefined;
    const localizedPlaceholder = locale !== 'en' ? prop[`placeholder_${locale}`] as string | undefined : undefined;
    const title = localizedTitle || (prop.title as string) || humanizeLabel(name);
    const placeholder = localizedPlaceholder ||
      (prop.placeholder as string | undefined) ||
      (prop.example != null ? String(prop.example) : undefined);
    const defaultVal = values?.[name] ?? prop.default;

    let fieldType: FormFieldDef['type'];
    let options: FormFieldDef['options'] | undefined;

    if (enumVals && enumVals.length > 0) {
      fieldType = 'select';
      options = enumVals.map((v, i) => ({ label: enumNames?.[i] ?? v, value: v }));
    } else if (propType === 'boolean') {
      fieldType = 'checkbox';
    } else if (propType === 'number' || propType === 'integer') {
      fieldType = 'number';
    } else if (propType === 'date' || format === 'date') {
      fieldType = 'date';
    } else if (propType === 'image' || format === 'binary' || (contentMediaType?.startsWith('image/'))) {
      fieldType = 'file';
    } else {
      fieldType = 'text';
    }

    fields.push({
      name,
      label: title,
      type: fieldType,
      required: requiredArr.includes(name),
      placeholder: placeholder || undefined,
      options,
      defaultValue: defaultVal as string | number | boolean | undefined,
      description: prop.description as string | undefined,
    });
  }

  return fields;
}
